import { Box, Button, CircularProgress, Divider, IconButton, Stack, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { DateField } from "@mui/x-date-pickers/DateField";
import RangeDay from "./RangeDay";
import { ACTIVE_BG_COLOR, ACTIVE_COLOR, CALENDAR_SLIDE_DURATION_MS, SHORTCUTS } from "./constants";
import { getShortcutLabel, normalizeValue } from "./utils";
import { useRangeCalendar } from "./useRangeCalendar";

const formatHeaderMonth = (month) => month.format("MMMM YYYY");

export default function RangeCalendar({ value, onApply, onClose, isLoading, hideClearButton = false }) {
  const {
    selectedShortcut,
    draftRange,
    leftMonth,
    rightMonth,
    hasRange,
    calendarSlideAnimation,
    handleSelectShortcut,
    handlePickDate,
    handleFromDateInputChange,
    handleToDateInputChange,
    handlePrevMonth,
    handleNextMonth,
    handleLeftMonthChange,
    handleRightMonthChange,
  } = useRangeCalendar({ value });

  const externalRange = normalizeValue(value);

  const applyRange = (nextRange) => {
    const rangeToApply = {
      fromDate: nextRange.fromDate || externalRange.fromDate,
      toDate: nextRange.toDate || nextRange.fromDate || externalRange.toDate || externalRange.fromDate,
    };

    onApply?.({
      fromDate: rangeToApply.fromDate?.toISOString(),
      toDate: rangeToApply.toDate?.toISOString(),
    });
  };

  const handleApplyClick = () => {
    if (!draftRange.fromDate && !draftRange.toDate) {
      onApply?.({ fromDate: undefined, toDate: undefined });

      return;
    }
    applyRange(draftRange);
  };

  const handleClearClick = () => {
    onApply?.({ fromDate: undefined, toDate: undefined });
  };

  const renderRangeDay = (props) => (
    <RangeDay
      {...props}
      draftRange={draftRange}
      hasRange={hasRange}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        maxHeight: "100%",
        flex: 1,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        mb={1}
        flexShrink={0}
        flexWrap="wrap"
        useFlexGap
      >
        <Button
          variant="outlined"
          size="small"
          sx={{ textTransform: "none", minWidth: "160px", justifyContent: "space-between" }}
        >
          {getShortcutLabel(selectedShortcut) || "Khoảng ngày"}
        </Button>

        <DateField
          value={draftRange.fromDate}
          onChange={handleFromDateInputChange}
          format="DD/MM/YYYY"
          slotProps={{
            textField: {
              size: "small",
              placeholder: "Từ ngày",
              sx: { minWidth: "145px" },
            },
          }}
        />
        <Typography fontSize="18px">→</Typography>
        <DateField
          value={draftRange.toDate || draftRange.fromDate}
          onChange={handleToDateInputChange}
          format="DD/MM/YYYY"
          slotProps={{
            textField: {
              size: "small",
              placeholder: "Đến ngày",
              sx: { minWidth: "145px" },
            },
          }}
        />
      </Stack>

      <Divider sx={{ mb: 1.5, flexShrink: 0 }} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
        >
          <Box
            sx={{
              width: 180,
              flexShrink: 0,
              borderRight: "1px solid var(--light-grey)",
              pr: 1,
            }}
          >
            {SHORTCUTS.map((shortcut) => (
              <Button
                key={shortcut.id}
                onClick={() => handleSelectShortcut(shortcut.id)}
                sx={{
                  width: "100%",
                  justifyContent: "flex-start",
                  textTransform: "none",
                  color: selectedShortcut === shortcut.id ? ACTIVE_COLOR : "var(--black)",
                  backgroundColor: selectedShortcut === shortcut.id ? ACTIVE_BG_COLOR : "transparent",
                  mb: 0.5,
                }}
              >
                {shortcut.label}
              </Button>
            ))}
          </Box>

          <Stack
            direction="row"
            spacing={1.5}
            flex={1}
            minWidth={0}
          >
          <Box flex={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              px={1}
              mb={0.5}
            >
              <IconButton
                size="small"
                onClick={handlePrevMonth}
              >
                <ChevronLeft />
              </IconButton>
              <Typography
                fontWeight={600}
                textTransform="capitalize"
              >
                {formatHeaderMonth(leftMonth)}
              </Typography>
              <Box width={34} />
            </Stack>
            <Box
              key={`left-${leftMonth.format("YYYY-MM")}`}
              sx={{
                animation: `${calendarSlideAnimation} ${CALENDAR_SLIDE_DURATION_MS}ms ease-out`,
                "@keyframes dateRangeSlideInLeft": {
                  from: { opacity: 0.7, transform: "translateX(18px)" },
                  to: { opacity: 1, transform: "translateX(0)" },
                },
                "@keyframes dateRangeSlideInRight": {
                  from: { opacity: 0.7, transform: "translateX(-18px)" },
                  to: { opacity: 1, transform: "translateX(0)" },
                },
              }}
            >
              <DateCalendar
                value={null}
                disableHighlightToday
                referenceDate={leftMonth}
                onChange={handlePickDate}
                onMonthChange={handleLeftMonthChange}
                slots={{ day: renderRangeDay, calendarHeader: () => null }}
                sx={{
                  width: "100%",
                }}
              />
            </Box>
          </Box>

          <Box
            flex={1}
            borderLeft="1px solid var(--light-grey)"
            pl={1.5}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              px={1}
              mb={0.5}
            >
              <Box width={34} />
              <Typography
                fontWeight={600}
                textTransform="capitalize"
              >
                {formatHeaderMonth(rightMonth)}
              </Typography>
              <IconButton
                size="small"
                onClick={handleNextMonth}
              >
                <ChevronRight />
              </IconButton>
            </Stack>
            <Box
              key={`right-${rightMonth.format("YYYY-MM")}`}
              sx={{
                animation: `${calendarSlideAnimation} ${CALENDAR_SLIDE_DURATION_MS}ms ease-out`,
                "@keyframes dateRangeSlideInLeft": {
                  from: { opacity: 0.7, transform: "translateX(18px)" },
                  to: { opacity: 1, transform: "translateX(0)" },
                },
                "@keyframes dateRangeSlideInRight": {
                  from: { opacity: 0.7, transform: "translateX(-18px)" },
                  to: { opacity: 1, transform: "translateX(0)" },
                },
              }}
            >
              <DateCalendar
                value={null}
                disableHighlightToday
                referenceDate={rightMonth}
                onChange={handlePickDate}
                onMonthChange={handleRightMonthChange}
                slots={{ day: renderRangeDay, calendarHeader: () => null }}
                sx={{
                  width: "100%",
                }}
              />
            </Box>
          </Box>
        </Stack>
        </Stack>
      </Box>

      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
        mt={1}
        flexShrink={0}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={onClose}
          sx={{ textTransform: "none" }}
        >
          Hủy
        </Button>
        {!hideClearButton && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearClick}
            sx={{ textTransform: "none" }}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : "Xóa"}
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          onClick={handleApplyClick}
          sx={{ textTransform: "none" }}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={20} /> : "Áp dụng"}
        </Button>
      </Stack>
    </Box>
  );
}
